import { z } from 'zod';
import { youtubeModeSchema } from './core.js';
/* ------------------------------------------------------------------ */
/* Hằng số                                                             */
/* ------------------------------------------------------------------ */
export const TOKEN_STATUSES = ['valid', 'expired', 'revoked'];
export const tokenStatusSchema = z.enum(TOKEN_STATUSES);
export const TOKEN_STATUS_LABEL = {
    valid: 'Token còn hiệu lực',
    expired: 'Token hết hạn',
    revoked: 'Đã thu hồi',
};
export const SYNC_RUN_STATUSES = ['running', 'ok', 'partial', 'failed'];
export const syncRunStatusSchema = z.enum(SYNC_RUN_STATUSES);
/** Tên job nền của module sync. */
export const SYNC_JOBS = [
    'stats',
    'analytics-daily',
    /*
     * Lấp các ngày còn trống trong 120 ngày gần nhất, chạy ngay sau
     * `analytics-daily`. Cần vì ba lý do thực tế: kênh mới kết nối (lượt lấy lịch
     * sử có thể hỏng giữa đường), kênh có token hỏng vài hôm rồi đăng nhập lại, và
     * những ngày YouTube trả rỗng lúc đó rồi sau mới có số.
     *
     * Thứ tự trong danh sách này quyết định thứ tự bảng "Lịch đồng bộ", nên giữ
     * cùng thứ tự với `LICH_JOB`.
     */
    'lap-ngay-thieu',
    'reach-daily',
    'revenue-daily',
    'revenue-finalize',
];
/**
 * Loại report của **YouTube Reporting API** cho impressions và CTR thumbnail.
 * Nguồn: https://developers.google.com/youtube/reporting/v1/reports/channel_reports
 *
 * `channel_reach_basic_a1` có dimension `date`, `channel_id`, `video_id` và hai
 * metric `video_thumbnail_impressions`, `video_thumbnail_impressions_ctr`.
 * `channel_reach_combined_a1` có cùng hai metric nhưng chẻ thêm theo nguồn lưu
 * lượng và thiết bị — dùng làm phương án dự phòng khi kênh không có bản basic.
 *
 * `youtubeAnalytics.reports.query` **không** có hai chỉ số này, nên không thể
 * lấy chúng bằng Analytics API.
 */
export const REPORT_REACH_UU_TIEN = [
    'channel_reach_basic_a1',
    'channel_reach_combined_a1',
];
/** Tên cột trong file CSV của reach report. */
export const COT_REACH = {
    date: 'date',
    channelId: 'channel_id',
    videoId: 'video_id',
    impressions: 'video_thumbnail_impressions',
    ctr: 'video_thumbnail_impressions_ctr',
};
/**
 * Mã kênh YouTube ở **chế độ mock**, suy từ handle.
 *
 * Đây là **nguồn duy nhất**: seed của module 01 và adapter mock của module 03
 * đều gọi hàm này. Trước đây seed ghi sẵn `UCmockRainForestSounds` còn adapter
 * tự sinh `UC-mock-rainforestsounds`, nên luồng "Đăng nhập lại" ở mock luôn
 * vướng lớp kiểm tra chọn nhầm Brand Account: `channels.list` (mock) trả một
 * mã, bảng `channels` giữ mã khác.
 *
 * Mã thật của YouTube là `UC` + 22 ký tự; ở mock ưu tiên đọc được để dễ tra khi
 * dev, nên dán luôn handle vào sau `UCmock`.
 */
export function maKenhMock(handle) {
    return `UCmock${handle.replace(/[^a-zA-Z0-9]/g, '')}`;
}
/**
 * Số video lấy số liệu **theo ngày** mỗi lượt đồng bộ.
 *
 * YouTube Analytics API không có báo cáo nào gộp `video` và `day` trong
 * `dimensions`: `video` là **filter**, không phải dimension (xem
 * https://developers.google.com/youtube/analytics/channel_reports — mục Video
 * reports). Muốn số liệu ngày của từng video thì phải gọi `dimensions=day` kèm
 * `filters=video==ID`, mỗi video một lượt gọi, nên phải chặn số lượt.
 *
 * Màn Phân tích chỉ hiện top 10 video, nên 25 là dư.
 */
export const SO_VIDEO_THEO_NGAY = 25;
/**
 * Khoảng `soNgay` ngày **kết thúc ở `ngayCuoi`**, kèm kỳ trước liền kề.
 *
 * `ngayCuoi` là ngày gần nhất **đã có dữ liệu**, không phải "hôm qua".
 * YouTube Analytics trễ 1–3 ngày (đo trên kênh thật ngày 08/10/2026: ngày mới
 * nhất là 05/10), nên lấy mốc "hôm qua" thì khung "28 ngày qua" của app gồm 2–3
 * ngày trống ở cuối và **ngắn hơn** 28 ngày thật của YouTube Studio — số liệu
 * thấp hơn Studio khoảng 3% mỗi ngày trống.
 */
export function khoangTuNgayCuoi(ngayCuoi, soNgay) {
    const den = new Date(`${ngayCuoi}T00:00:00.000Z`);
    const tu = new Date(den);
    tu.setUTCDate(tu.getUTCDate() - (soNgay - 1));
    const prevDen = new Date(tu);
    prevDen.setUTCDate(prevDen.getUTCDate() - 1);
    const prevTu = new Date(prevDen);
    prevTu.setUTCDate(prevTu.getUTCDate() - (soNgay - 1));
    const khoa = (d) => d.toISOString().slice(0, 10);
    return {
        from: khoa(tu),
        to: khoa(den),
        prevFrom: khoa(prevTu),
        prevTo: khoa(prevDen),
        days: soNgay,
    };
}
/** Nhãn khoảng ngày cho giao diện: `09/09 – 06/10/2026`. */
export function nhanKhoangNgay(from, to) {
    const [, thangTu, ngayTu] = from.split('-');
    const [namDen, thangDen, ngayDen] = to.split('-');
    return `${ngayTu}/${thangTu} – ${ngayDen}/${thangDen}/${namDen}`;
}
/** Quota mặc định mỗi ngày của YouTube Data API. */
export const DEFAULT_DAILY_QUOTA = 10_000;
/** Khoá ngày của YouTube, dạng YYYY-MM-DD theo giờ Thái Bình Dương. */
export const dateKeySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày phải dạng YYYY-MM-DD');
export const GRANULARITIES = ['day', 'week', 'month'];
export const granularitySchema = z.enum(GRANULARITIES);
/* ------------------------------------------------------------------ */
/* Kết nối                                                             */
/* ------------------------------------------------------------------ */
/**
 * Vì sao màn Kết nối API chưa có doanh thu qua API.
 *
 * Trước đây chỉ có cờ `revenueAvailable`, nên mọi trường hợp "chưa có tiền" đều
 * bị gán một lý do duy nhất là "Network ẩn → dùng sao kê". Sai với hai trường
 * hợp rất thường gặp: **chưa chạy job** doanh thu lần nào, và kênh **chưa được
 * YouTube bật kiếm tiền** (lúc đó không có tiền để ẩn).
 */
export const TRANG_THAI_DOANH_THU = [
    'co',
    'chua-dong-bo',
    'chua-bat-kiem-tien',
    'khong-co-quyen',
];
export const trangThaiDoanhThuSchema = z.enum(TRANG_THAI_DOANH_THU);
export const NHAN_TRANG_THAI_DOANH_THU = {
    co: 'Có',
    'chua-dong-bo': 'Chưa đồng bộ doanh thu',
    'chua-bat-kiem-tien': 'Kênh chưa bật kiếm tiền',
    'khong-co-quyen': 'Network ẩn → dùng sao kê',
};
/** Giải thích dài hơn, dùng cho `title` của ô trong bảng. */
export const MO_TA_TRANG_THAI_DOANH_THU = {
    co: 'YouTube trả số liệu doanh thu cho kênh này',
    'chua-dong-bo': 'Job doanh thu chưa chạy cho kênh này lần nào',
    'chua-bat-kiem-tien': 'Kênh chưa được YouTube bật kiếm tiền nên không có doanh thu để lấy — không liên quan tới Network',
    'khong-co-quyen': 'YouTube trả 403 cho truy vấn doanh thu: Network giữ quyền xem tiền, hoặc tài khoản không có quyền đó',
};
/**
 * Đổi `lastError` của kết nối thành một câu người dùng đọc được.
 *
 * Giao diện từng để cột "Đồng bộ" trống khi job lỗi mà token vẫn `valid` —
 * người dùng thấy kênh "Hợp lệ" nhưng không có số liệu và không biết vì sao.
 *
 * Trường hợp hay gặp nhất là **401 Invalid Credentials của YouTube Data API**:
 * YouTube Analytics API vẫn trả số liệu (truy vấn theo mã kênh), nhưng Data API
 * đòi token gắn đúng kênh, nên tài khoản chọn sai profile lúc đồng ý sẽ bị từ
 * chối. Đăng nhập lại và chọn đúng Brand Account là xong — không phải lỗi code.
 */
export function lyDoLoiDongBo(lastError) {
    if (!lastError)
        return null;
    if (/invalid authentication credentials|invalid credentials|unauthenticated/i.test(lastError)) {
        return ('Google từ chối YouTube Data API (401 Invalid Credentials): tài khoản đồng ý OAuth không ' +
            'gắn đúng kênh. Bấm "Đăng nhập lại" rồi chọn đúng Brand Account quản lý kênh này.');
    }
    if (/insufficient.*(permission|scope)|forbidden|(^|\D)403(\D|$)/i.test(lastError)) {
        return ('Google trả 403: tài khoản không phải chủ sở hữu hoặc người quản lý kênh, hoặc chưa cấp đủ ' +
            'quyền. Đăng nhập lại và cấp đủ quyền ở màn hình đồng ý của Google.');
    }
    if (/invalid_grant|token has been expired or revoked/i.test(lastError)) {
        return 'Google đã thu hồi quyền của app (invalid_grant). Cần đăng nhập lại.';
    }
    return lastError.length > 200 ? `${lastError.slice(0, 200)}…` : lastError;
}
/**
 * Tiến độ lấy lịch sử của một kênh và số ngày đã có số liệu.
 *
 * Giao diện cần cả hai: nhãn "Đang lấy lịch sử · x/120 ngày" khi job còn chạy,
 * và chú thích "chưa đủ dữ liệu" trên thẻ 28/90 ngày khi số ngày có số liệu ít
 * hơn số ngày của kỳ — lúc đó phần trăm so kỳ trước không có nghĩa.
 */
export const tienDoLichSuSchema = z.object({
    soNgayCan: z.number().int().positive(),
    soNgayDaCo: z.number().int().nonnegative(),
    /** Job lấy lịch sử đang chạy (hoặc đang chờ trong hàng đợi). */
    dangChay: z.boolean(),
});
/** Kỳ nào đủ dữ liệu, kỳ nào chưa — dùng để ẩn % và hiện chú thích. */
export function duDuLieuChoKy(soNgayDaCo, soNgayCuaKy) {
    return soNgayDaCo >= soNgayCuaKy;
}
/** Kết quả thu hồi kết nối: giao diện cần biết Google có nhận lệnh hay chưa. */
export const ketQuaThuHoiSchema = z.object({
    daThuHoiTaiGoogle: z.boolean(),
    /** Lý do khi Google từ chối hoặc khi app cố tình không gọi revoke. */
    lyDo: z.string().nullable(),
    /**
     * App **cố tình không gọi** revoke vì còn kênh khác dùng cùng tài khoản
     * Google. Khác hẳn "Google từ chối": đây là lựa chọn an toàn, không phải lỗi.
     */
    boQuaRevoke: z.boolean(),
});
/** Kết quả gỡ kênh khỏi danh sách Kết nối API. */
export const ketQuaGoKhoiApiSchema = z.object({
    /** Kênh có kết nối nên app đã thu hồi; `false` khi kênh chưa kết nối. */
    daThuHoiKetNoi: z.boolean(),
    thuHoi: ketQuaThuHoiSchema.nullable(),
});
export const connectionSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    /// Màu avatar của kênh, lấy từ module 01 để giao diện khỏi tự đoán màu.
    channelAvatarColor: z.string(),
    /// Email đã che. Không bao giờ trả refresh token ra API.
    googleAccountLabel: z.string(),
    scopes: z.array(z.string()),
    tokenStatus: tokenStatusSchema,
    connectedAt: z.iso.datetime(),
    lastRefreshAt: z.iso.datetime().nullable(),
    lastError: z.string().nullable(),
    /**
     * Lượt gọi YouTube Data API gần nhất có thành công hay không (`null` = chưa
     * gọi). Cột "Token" chỉ ghi "Hợp lệ" khi lượt đó thành công.
     */
    dataApiOk: z.boolean().nullable(),
    /**
     * Số kênh (kể cả kênh này) đang dùng **cùng một tài khoản Google**.
     *
     * Chỉ là ghi chú, không phải cảnh báo: lỗi 401 trước đây do app dùng chung
     * đối tượng `google` giữa các lượt gọi, đã sửa (xem docs/modules/sync.md).
     */
    soKenhCungTaiKhoan: z.number().int().positive(),
    /** Token cấp trước khi thêm scope `openid`/`email` — nên đăng nhập lại. */
    thieuScopeMoi: z.boolean(),
    /** Tiến độ số ngày có số liệu, `null` khi chưa có lượt lấy lịch sử nào. */
    lichSu: tienDoLichSuSchema.nullable(),
    /**
     * Nhãn **tài khoản quản lý kênh**, lấy từ hồ sơ kênh (module 01) và đã che.
     *
     * Khác `googleAccountLabel`: cái kia là tài khoản đã **đồng ý OAuth** (Brand
     * Account hoặc tài khoản đăng nhập), cái này là tài khoản người dùng khai để
     * biết ai quản lý kênh. `null` khi hồ sơ kênh chưa nhập.
     */
    managerLabel: z.string().nullable(),
    /// Kênh đã kết nối nhưng YouTube chưa trả dữ liệu doanh thu.
    revenueAvailable: z.boolean(),
    /// Vì sao chưa có doanh thu qua API. `co` nghĩa là có.
    revenueState: trangThaiDoanhThuSchema,
});
/** Kết quả `getSyncStatus(channelId)`, cũng là enricher `syncStatus` của channels. */
export const syncStatusSchema = z.object({
    connected: z.boolean(),
    lastSyncAt: z.iso.datetime().nullable(),
    message: z.string().nullable(),
    /** `null` khi kênh chưa kết nối hoặc chưa có lượt lấy lịch sử nào. */
    lichSu: tienDoLichSuSchema.nullable(),
});
export const oauthStartQuerySchema = z.object({ channelId: z.string().min(1) });
export const oauthStartResponseSchema = z.object({ url: z.url() });
/* ------------------------------------------------------------------ */
/* Số liệu                                                             */
/* ------------------------------------------------------------------ */
export const metricsRangeSchema = z.object({
    channelIds: z.array(z.string()).optional(),
    from: dateKeySchema,
    to: dateKeySchema,
    granularity: granularitySchema.default('day'),
});
/** Một điểm số liệu của kênh trong một khoảng (ngày, tuần hoặc tháng). */
export const channelMetricPointSchema = z.object({
    channelId: z.string(),
    /// Khoá khoảng: YYYY-MM-DD (day), YYYY-Www (week) hoặc YYYY-MM (month).
    date: z.string(),
    /// Tổng lượt xem của **những ngày có số liệu** trong khoảng gộp.
    views: z.number().int().nonnegative(),
    estimatedMinutesWatched: z.number().int().nonnegative(),
    averageViewDuration: z.number().int().nonnegative(),
    subscribersGained: z.number().int().nonnegative(),
    subscribersLost: z.number().int().nonnegative(),
    /// Tổng lượt hiển thị của **những ngày có số** (`impressions IS NOT NULL`).
    impressions: z.number().int().nonnegative(),
    impressionsCtr: z.number(),
    /// `true` khi ngày chưa đủ 3 ngày nên số liệu có thể còn đổi.
    provisional: z.boolean(),
    /**
     * Số ngày trong khoảng gộp **thật sự có** lượt xem (`views IS NOT NULL`).
     *
     * `0` nghĩa là "chưa có số liệu lượt xem", khác hẳn `views = 0` nghĩa là
     * "YouTube trả 0 lượt xem". Dòng ngày còn được hàm ghi doanh thu và hàm ghi
     * lượt hiển thị tạo ra, nên một khoảng có thể có dòng mà không có lượt xem.
     */
    soNgayCoViews: z.number().int().nonnegative(),
    /// Số ngày có **dòng** trong khoảng gộp, dù dòng đó có lượt xem hay không.
    soNgayCoDong: z.number().int().nonnegative(),
    /**
     * Số ngày trong khoảng gộp **thật sự có** lượt hiển thị (`impressions > 0`).
     *
     * `0` ⇒ `impressionsCtr` là "chưa có số liệu", giao diện phải hiện `—` chứ
     * không hiện `0 %`. Lượt hiển thị chỉ có từ ngày app tạo reporting job, và
     * Reporting API không cấp số của quá khứ.
     *
     * Ngày báo cáo nói `impressions = 0` **không** được tính: đó là số thật (vẫn
     * nằm trong tổng `impressions`) nhưng CTR của nó là `0/0`, không xác định.
     */
    soNgayCoImpressions: z.number().int().nonnegative(),
});
export const videoMetricPointSchema = z.object({
    videoId: z.string(),
    youtubeVideoId: z.string(),
    channelId: z.string(),
    date: z.string(),
    views: z.number().int().nonnegative(),
    estimatedMinutesWatched: z.number().int().nonnegative(),
    averageViewDuration: z.number().int().nonnegative(),
    /// `null` = ngày đó chưa có số của Reporting API (xem `videoTotalsSchema`).
    impressions: z.number().int().nonnegative().nullable(),
    impressionsCtr: z.number().nullable(),
});
export const trafficSourcePointSchema = z.object({
    channelId: z.string(),
    date: z.string(),
    sourceType: z.string(),
    views: z.number().int().nonnegative(),
});
/** Kết quả `getRevenue(channelIds, from, to)`, mỗi kênh một dòng. */
export const revenueResultSchema = z.object({
    channelId: z.string(),
    gross: z.string(),
    currency: z.string(),
    /// `false` khi chưa có quyền xem doanh thu hoặc YouTube chưa trả dữ liệu.
    available: z.boolean(),
    /// `true` sau khi job revenue-finalize chạy lại cả tháng.
    isFinal: z.boolean(),
});
/** Chỉ số 28 ngày dùng cho enricher `metrics` của channels. */
export const channelShortMetricsSchema = z.object({
    subscribers: z.number().int().nonnegative(),
    /// `null` = **chưa có số liệu lượt xem** cho kỳ này; giao diện hiện `—`.
    views: z.number().int().nonnegative().nullable(),
    /// Số ngày trong kỳ có lượt xem thật, để giao diện ghi "chỉ tính N/28 ngày".
    soNgayCoViews: z.number().int().nonnegative(),
    /// Số ngày của kỳ.
    soNgayTrongKy: z.number().int().nonnegative(),
});
/**
 * Mẫu số của RPM cho một nhóm kênh trong một khoảng.
 *
 * Chỉ gộp những ngày có **cả** doanh thu và lượt xem > 0: trộn ngày có tiền mà
 * thiếu lượt xem vào phép chia cho ra những con số như 621,81 $/1000 lượt xem.
 */
export const mauSoRpmSchema = z.object({
    /// Tổng doanh thu gộp của các ngày đủ điều kiện.
    gross: z.number(),
    /// Tổng lượt xem của các ngày đủ điều kiện.
    views: z.number().int().nonnegative(),
    /// Số ngày đủ điều kiện. `0` ⇒ `rpm = null` và giao diện hiện `—`.
    soNgay: z.number().int().nonnegative(),
    /// `null` khi `soNgay = 0`.
    rpm: z.number().nullable(),
});
/**
 * Doanh thu của **một kênh trong một khoảng** đã gộp theo `granularity`.
 *
 * Hai cặp số vì hai chỉ số tiền có hai mẫu số khác nhau: `gross` cộng mọi ngày
 * `revenueAvailable`, còn ba cột `…Rpm` chỉ cộng ngày có **cả** tiền và lượt
 * xem > 0 — cùng luật với {@link mauSoRpmSchema}.
 */
export const diemDoanhThuKenhSchema = z.object({
    channelId: z.string(),
    /// Khoá khoảng: YYYY-MM-DD (day), YYYY-Www (week) hoặc YYYY-MM (month).
    date: z.string(),
    /// Doanh thu gộp của mọi ngày có doanh thu khả dụng trong khoảng.
    gross: z.number(),
    /// `0` ⇒ khoảng này **chưa có** doanh thu, giao diện hiện `—` chứ không `0 $`.
    soNgayCoTien: z.number().int().nonnegative(),
    /// Tử số RPM: doanh thu của các ngày đủ điều kiện.
    grossRpm: z.number(),
    /// Mẫu số RPM: lượt xem của các ngày đủ điều kiện.
    viewsRpm: z.number().int().nonnegative(),
    /// `0` ⇒ RPM của khoảng này là **chưa biết**.
    soNgayRpm: z.number().int().nonnegative(),
    currency: z.string(),
});
/* ------------------------------------------------------------------ */
/* Video, quota, lịch sử                                               */
/* ------------------------------------------------------------------ */
export const uploadVideoInputSchema = z.object({
    channelId: z.string().min(1),
    /// Đường dẫn file trong `storage/` do module production tạo.
    videoFilePath: z.string().min(1),
    title: z.string().min(1).max(100),
    description: z.string().max(5000).default(''),
    tags: z.array(z.string()).max(50).default([]),
    /// Giờ công khai (UTC). Upload với privacyStatus=private rồi đặt publishAt.
    publishAt: z.iso.datetime().nullish(),
    thumbnailPath: z.string().nullish(),
    /// Khai báo nội dung tạo bằng AI.
    aiDisclosure: z.boolean().default(false),
});
export const uploadVideoResultSchema = z.object({
    youtubeVideoId: z.string(),
    privacyStatus: z.string(),
    publishAt: z.iso.datetime().nullable(),
    quotaUnitsUsed: z.number().int().nonnegative(),
});
export const updateVideoMetadataInputSchema = z.object({
    youtubeVideoId: z.string().min(1),
    title: z.string().max(100).optional(),
    description: z.string().max(5000).optional(),
    tags: z.array(z.string()).max(50).optional(),
    thumbnailPath: z.string().nullish(),
    privacyStatus: z.enum(['private', 'unlisted', 'public']).optional(),
});
export const quotaUsageSchema = z.object({
    date: z.string(),
    units: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    percent: z.number(),
    byOperation: z.record(z.string(), z.number()).nullable(),
});
export const syncRunSchema = z.object({
    id: z.string(),
    job: z.string(),
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    status: syncRunStatusSchema,
    channelsOk: z.number().int().nonnegative(),
    channelsFailed: z.number().int().nonnegative(),
});
/** 4 thẻ chỉ số của màn hình Kết nối API. */
export const syncSummarySchema = z.object({
    connectedChannels: z.number().int().nonnegative(),
    totalChannels: z.number().int().nonnegative(),
    lastSyncAt: z.iso.datetime().nullable(),
    quota: quotaUsageSchema,
    /// Chế độ dữ liệu YouTube hiện tại.
    mode: youtubeModeSchema,
    /**
     * Trạng thái ứng dụng OAuth trên Google Cloud Console (`GOOGLE_OAUTH_STATUS`).
     *
     * `testing`: màn hình đồng ý còn ở chế độ Testing, refresh token hết hạn sau
     * 7 ngày. `production`: đã bấm "Publish app" — token không còn hết hạn theo
     * 7 ngày, dù app có thể vẫn đang chờ xác minh.
     */
    oauthStatus: z.enum(['testing', 'production']),
    expiredTokens: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------------ */
/* Bổ sung ở module 03 (chỉ thêm, không đổi phần đã chốt 01b)           */
/* ------------------------------------------------------------------ */
/** Màu badge cho trạng thái token. */
export const TOKEN_STATUS_TONE = {
    valid: 'ok',
    expired: 'warn',
    revoked: 'bad',
};
export const SYNC_RUN_STATUS_LABEL = {
    running: 'Đang chạy',
    ok: 'Thành công',
    partial: 'Một phần',
    failed: 'Thất bại',
};
export const SYNC_RUN_STATUS_TONE = {
    running: 'info',
    ok: 'ok',
    partial: 'warn',
    failed: 'bad',
};
export const SYNC_JOB_LABEL = {
    stats: 'Số liệu kênh và video',
    'analytics-daily': 'Analytics theo ngày',
    'lap-ngay-thieu': 'Lấp ngày trống (120 ngày gần nhất)',
    'reach-daily': 'Impressions và CTR thumbnail',
    'revenue-daily': 'Doanh thu theo ngày',
    'revenue-finalize': 'Chốt doanh thu tháng',
};
/**
 * Scope OAuth của app. Tên lấy từ tài liệu chính thức của Google:
 * https://developers.google.com/identity/protocols/oauth2/scopes
 */
export const YOUTUBE_SCOPES = [
    'https://www.googleapis.com/auth/youtube.readonly',
    'https://www.googleapis.com/auth/yt-analytics.readonly',
    'https://www.googleapis.com/auth/yt-analytics-monetary.readonly',
    'https://www.googleapis.com/auth/youtube.upload',
    'https://www.googleapis.com/auth/youtube.force-ssl',
    /*
     * Hai scope **không nhạy cảm**, chỉ để biết **ai** đã đồng ý: không có chúng
     * thì `tokeninfo` không trả email lẫn `sub`, nên cột "Tài khoản Google" rơi
     * về giá trị dự phòng `khong-ro@gmail.com` (hiện ra là `kho••••@gmail.com`).
     * Google không coi `openid` và `userinfo.email` là sensitive scope nên không
     * làm khó thêm cho bước xác minh ứng dụng.
     */
    'openid',
    'https://www.googleapis.com/auth/userinfo.email',
];
/**
 * Scope thêm vào sau khi một số kênh đã kết nối.
 *
 * Token cấp trước đó thiếu chúng: vẫn đồng bộ được bình thường, chỉ là không
 * biết email tài khoản — giao diện nhắc "Cần đăng nhập lại để cập nhật quyền".
 */
export const SCOPE_MOI_THEM = ['openid', 'https://www.googleapis.com/auth/userinfo.email'];
/** Kết nối này đã có đủ scope mới chưa. */
export function thieuScopeMoi(scopes) {
    return SCOPE_MOI_THEM.some((s) => !scopes.includes(s));
}
/** Bốn nhóm quyền hiển thị thành chip trên màn hình Kết nối API. */
export const SCOPE_GROUPS = [
    { key: 'data', label: 'Data', scopes: ['https://www.googleapis.com/auth/youtube.readonly'] },
    {
        key: 'analytics',
        label: 'Analytics',
        scopes: ['https://www.googleapis.com/auth/yt-analytics.readonly'],
    },
    {
        key: 'revenue',
        label: 'Doanh thu',
        scopes: ['https://www.googleapis.com/auth/yt-analytics-monetary.readonly'],
    },
    {
        key: 'upload',
        label: 'Upload',
        scopes: [
            'https://www.googleapis.com/auth/youtube.upload',
            'https://www.googleapis.com/auth/youtube.force-ssl',
        ],
    },
    {
        key: 'danh-tinh',
        label: 'Email',
        scopes: ['openid', 'https://www.googleapis.com/auth/userinfo.email'],
    },
];
/**
 * Chi phí quota của YouTube Data API v3 (đơn vị cho rổ chung 10.000/ngày).
 * Nguồn: https://developers.google.com/youtube/v3/determine_quota_cost
 * Từ 04/12/2025 `videos.insert` và `search.list` có rổ riêng tính theo **lượt gọi**
 * (100 lượt/ngày), không trừ vào rổ chung.
 */
export const CHI_PHI_QUOTA = {
    'channels.list': 1,
    'videos.list': 1,
    'playlistItems.list': 1,
    'playlists.list': 1,
    'videos.update': 50,
    'thumbnails.set': 50,
    'videos.insert': 0, // rổ riêng "Video uploads"
    'search.list': 0, // rổ riêng "Search queries"
    'reports.query': 0, // YouTube Analytics API — hạn mức riêng, không thuộc rổ 10.000
    // YouTube Reporting API cũng có hạn mức riêng, không trừ vào rổ 10.000 của
    // Data API. Vẫn ghi lại số lượt gọi để màn Kết nối API thấy job đã chạy.
    'reportTypes.list': 0,
    'reportingJobs.create': 0,
    'reportingJobs.list': 0,
    'reports.list': 0,
    'reports.download': 0,
};
/** Số lượt gọi tối đa mỗi ngày của các rổ riêng. */
export const QUOTA_RO_RIENG = {
    'videos.insert': 100,
    'search.list': 100,
};
/** Vượt ngưỡng này thì tạm dừng các job không quan trọng. */
export const NGUONG_TAM_DUNG_QUOTA = 0.8;
export const videoSummarySchema = z.object({
    videoId: z.string(),
    youtubeVideoId: z.string(),
    channelId: z.string(),
    channelName: z.string(),
    title: z.string(),
    thumbnailUrl: z.string().nullable(),
    publishedAt: z.iso.datetime().nullable(),
    durationSec: z.number().int().nonnegative(),
});
/** Tổng số liệu của một video trong khoảng, dùng cho bảng video nổi bật. */
export const videoTotalsSchema = z.object({
    videoId: z.string(),
    views: z.number().int().nonnegative(),
    estimatedMinutesWatched: z.number().int().nonnegative(),
    /**
     * Tổng lượt hiển thị của những ngày có số; `null` = **chưa có số liệu**.
     *
     * Lượt hiển thị chỉ đến từ Reporting API (job `reach-daily`) và chỉ có từ
     * ngày app tạo reporting job, nên video cũ hoàn toàn có thể không có số nào.
     * `null` ⇒ giao diện hiện `—`, không hiện `0`.
     */
    impressions: z.number().int().nonnegative().nullable(),
    /**
     * CTR thumbnail, phần trăm (ví dụ `5.2`); `null` khi chưa có lượt hiển thị.
     *
     * Số của YouTube (`video_thumbnail_impressions_ctr` trong reach report của
     * Reporting API), **không** phải `views / impressions`: lượt xem còn đến từ
     * playlist, link ngoài hay tự phát nên không phải lượt xem nào cũng bắt đầu
     * bằng một cú bấm vào thumbnail.
     */
    impressionsCtr: z.number().nullable(),
    estimatedRevenue: z.string().nullable(),
    currency: z.string(),
    provisional: z.boolean(),
});
/** Một dòng nhật ký đồng bộ, trải phẳng từ `SyncRun.log`. */
export const syncLogEntrySchema = z.object({
    id: z.string(),
    job: z.string(),
    at: z.iso.datetime(),
    level: z.enum(['info', 'warn', 'error']),
    channelName: z.string().nullable(),
    message: z.string(),
});
/** Một dòng của bảng "Lịch đồng bộ". */
export const syncScheduleRowSchema = z.object({
    job: z.string(),
    label: z.string(),
    cron: z.string(),
    moTa: z.string(),
    lastRunAt: z.iso.datetime().nullable(),
    lastStatus: syncRunStatusSchema.nullable(),
});
export const dongBoNgayInputSchema = z.object({
    /** Không truyền là đồng bộ mọi kênh đã kết nối. */
    channelIds: z.array(z.string()).optional(),
    job: z.enum(SYNC_JOBS).default('stats'),
});
export const dongBoNgayResultSchema = z.object({
    syncRunId: z.string(),
    job: z.string(),
    channelsOk: z.number().int().nonnegative(),
    channelsFailed: z.number().int().nonnegative(),
    status: syncRunStatusSchema,
});
/** Kênh chưa kết nối OAuth, để bảng Kết nối từng kênh hiện đủ 8 dòng. */
export const kenhChuaKetNoiSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    channelAvatarColor: z.string(),
});
/* ------------------------------------------------------------------ */
/* Link mời kết nối YouTube                                            */
/* ------------------------------------------------------------------ */
/**
 * Link mời để **chủ kênh tự cấp quyền** mà không cần tài khoản trong app.
 *
 * Token thật chỉ nằm trong URL và **chỉ xuất hiện đúng một lần**, lúc tạo:
 * database chỉ giữ `sha256(token)` nên mất link là phải tạo link mới, không ai
 * đọc lại được từ bảng.
 */
export const TRANG_THAI_LOI_MOI_OAUTH = ['pending', 'used', 'revoked', 'expired'];
export const trangThaiLoiMoiOauthSchema = z.enum(TRANG_THAI_LOI_MOI_OAUTH);
export const NHAN_TRANG_THAI_LOI_MOI_OAUTH = {
    pending: 'Đang hiệu lực',
    used: 'Đã dùng',
    revoked: 'Đã thu hồi',
    expired: 'Hết hạn',
};
/** Hạn của một link mời, tính bằng giờ. */
export const HAN_LOI_MOI_OAUTH_GIO = 48;
export const taoLoiMoiOauthInputSchema = z.object({ channelId: z.string().min(1) });
export const loiMoiOauthSchema = z.object({
    id: z.string(),
    channelId: z.string(),
    status: trangThaiLoiMoiOauthSchema,
    createdAt: z.string(),
    expiresAt: z.string(),
    /** Tên người tạo link; `null` khi tài khoản đó đã bị xoá. */
    createdByName: z.string().nullable(),
    usedAt: z.string().nullable(),
    /** Email Google đã cấp quyền, đã che; `null` khi link chưa dùng. */
    usedByEmail: z.string().nullable(),
});
/** Kết quả tạo link: `url` **chỉ** trả ở đây, không có endpoint nào đọc lại. */
export const loiMoiOauthDaTaoSchema = loiMoiOauthSchema.extend({ url: z.string() });
/**
 * Dữ liệu trang công khai `/ket-noi/:token`.
 *
 * Chỉ những gì chủ kênh cần thấy để biết mình đang cấp quyền cho đúng kênh:
 * **không** có id lời mời, không có người tạo, không có hạn tuyệt đối của bản
 * ghi — token sai hay hết hạn đều trả cùng một lỗi chung.
 */
export const loiMoiCongKhaiSchema = z.object({
    channelName: z.string(),
    channelHandle: z.string(),
    channelAvatarColor: z.string(),
    /** Nhóm quyền sẽ cấp, lấy từ `SCOPE_GROUPS`. */
    quyen: z.array(z.object({ key: z.string(), label: z.string(), moTa: z.string() })),
});
/** Mô tả bằng tiếng Việt từng nhóm quyền, cho trang công khai. */
export const MO_TA_NHOM_QUYEN = {
    data: 'Đọc thông tin kênh và danh sách video',
    analytics: 'Đọc số liệu lượt xem, giờ xem, người đăng ký',
    revenue: 'Đọc số liệu doanh thu để đối soát với Network',
    upload: 'Đăng video và sửa thông tin video theo lịch đã duyệt',
    'danh-tinh': 'Biết email tài khoản đã cấp quyền, để ghi nhật ký',
};
//# sourceMappingURL=sync.js.map